import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { SovosBox, SovosSnackbar } from "mosaic-react";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import { trackUserAction } from "../../../../utils/pendo";
import { DO_NOT_IMPORT, FLOW_STEP_INDEX } from "./constants";
import { buildDgItems, resolveAiTargetToGridFieldId, stripCdmPrefix } from "./utils";
import { sanitizeDisplayValue, toSourceColumnLabel } from "../mappingDisplayLabels";
import ChooseEntityCard from "./components/ChooseEntityCard";
import ConfirmDialog from "./components/ConfirmDialog";
import InlineLoadError from "./components/InlineLoadError";
import StepMapColumns from "./steps/StepMapColumns";
import WarningSkipDialog from "./components/WarningSkipDialog";

const SYSTEM_COLUMNS = new Set(["CLB_ImportID", "FieldMetadata"]);
const draftKey = (profileId) => `column_mapping_draft_${profileId}`;

const saveDraft = (profileId, draft) => {
    try {
        sessionStorage.setItem(draftKey(profileId), JSON.stringify(draft));
    } catch (_e) {
        // sessionStorage unavailable (private browsing, quota exceeded) — silently skip
    }
};

const loadDraft = (profileId) => {
    try {
        const raw = sessionStorage.getItem(draftKey(profileId));
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const clearDraft = (profileId) => {
    try {
        sessionStorage.removeItem(draftKey(profileId));
    } catch (_e) {
        // sessionStorage unavailable — silently skip
    }
};

const toMschFields = (items) =>
    (items || []).map((field) => ({
        id: field.cdm_name,
        name: sanitizeDisplayValue(field.db_name),
        type:
            field.field_type?.startsWith("int") || field.field_type?.startsWith("decimal")
                ? "number"
                : field.field_type?.startsWith("date")
                  ? "date"
                  : "text",
        nullable: field.nullable,
    }));

const ColumnMappingCanvas = ({
    frameworkName = "",
    items = [],
    headerItems = [],
    columnsWithData = null,
    isActive = true,
    profileId,
    projectId,
    fiscalYear,
    importId,
    onFetchMappingContexts,
    onFetchEntities,
    onFetchCdmFields,
    onFetchExistingMapping,
    onCreateMapping,
    onUpdateMapping,
    onSuggestEntity,
    onSuggestMapping,
    projectModules,
    onStepChange,
    onActionStateChange,
    onMappingSaved,
    onMappingConfirmed,
}) => {
    const { i18n } = useGlobalValue();
    const userHeaderItems = useMemo(
        () => headerItems.filter((header) => !SYSTEM_COLUMNS.has(header.headerName)),
        [headerItems],
    );
    const csvColumns = useMemo(
        () => userHeaderItems.map((header) => header.columnName),
        [userHeaderItems],
    );
    const [step, setStep] = useState(FLOW_STEP_INDEX.ChooseEntity);
    const [entityCanProceed, setEntityCanProceed] = useState(false);
    const [entityLabel, setEntityLabel] = useState("");
    const [selectedConfigSet, setSelectedConfigSet] = useState("");
    const [selectedEntityName, setSelectedEntityName] = useState("");
    const [selectedFileType, setSelectedFileType] = useState("");
    const [mschFields, setMschFields] = useState([]);
    const [mappings, setMappings] = useState({});
    const mappingsRef = useRef(mappings);
    const originalMappingsRef = useRef({});
    const [hideDoNotImport, setHideDoNotImport] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSkipProceedDialog, setShowSkipProceedDialog] = useState(false);
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const [imported, setImported] = useState(false);
    const [savedMappingId, setSavedMappingId] = useState(null);
    const [savingMapping, setSavingMapping] = useState(false);
    const [loadingAiMapping, setLoadingAiMapping] = useState(false);
    const [aiSuggestionMeta, setAiSuggestionMeta] = useState(new Map());
    const [aiSuggestedColumns, setAiSuggestedColumns] = useState(new Map());
    const aiMappingRequestRef = useRef(0);
    const [showAiAppliedToast, setShowAiAppliedToast] = useState(false);
    const [aiAppliedCount, setAiAppliedCount] = useState(0);
    const [aiAppliedSkippedCsv, setAiAppliedSkippedCsv] = useState(0);
    const [mappingSuggestError, setMappingSuggestError] = useState(null);
    const [loadingMschFields, setLoadingMschFields] = useState(false);
    const [persistedPreloadError, setPersistedPreloadError] = useState(false);
    const [persistedPreloadPending, setPersistedPreloadPending] = useState(false);
    const persistedPreloadRef = useRef(null);
    const persistedPreloadRequestRef = useRef(0);
    const entityFieldsRequestRef = useRef(0);
    const entitySelectionRef = useRef(null);
    const [loadingExistingMapping, setLoadingExistingMapping] = useState(
        !!profileId && !!onFetchExistingMapping,
    );
    const [isFirstMapping, setIsFirstMapping] = useState(false);

    useEffect(() => {
        mappingsRef.current = mappings;
    }, [mappings]);

    const preloadPersistedFields = useCallback(
        async ({ existing, restoredMappings }) => {
            const requestId = ++persistedPreloadRequestRef.current;
            setLoadingMschFields(true);
            setPersistedPreloadPending(true);
            setPersistedPreloadError(false);
            try {
                const result = await onFetchCdmFields(
                    existing.config_set,
                    existing.entity_name,
                    existing.file_type,
                );
                if (requestId !== persistedPreloadRequestRef.current) return;
                const fields = toMschFields(result?.items);
                if (!fields.length) throw new Error("No target fields available");
                if (result?.file_type) setSelectedFileType(result.file_type);
                const normalizedMappings = Object.fromEntries(
                    Object.entries(restoredMappings).map(([sourceColumn, targetField]) => [
                        sourceColumn,
                        resolveAiTargetToGridFieldId(targetField, fields),
                    ]),
                );
                setMappings(normalizedMappings);
                originalMappingsRef.current = normalizedMappings;
                setMschFields(fields);
            } catch (error) {
                if (requestId !== persistedPreloadRequestRef.current) return;
                setMappings(restoredMappings);
                originalMappingsRef.current = restoredMappings;
                setMschFields([]);
                setPersistedPreloadError(true);
            } finally {
                if (requestId === persistedPreloadRequestRef.current) {
                    setLoadingMschFields(false);
                    setPersistedPreloadPending(false);
                }
            }
        },
        [onFetchCdmFields],
    );

    // This canvas stays mounted (hidden via CSS) when the user switches to the
    // Staging Data tab, so any open dialogs would otherwise keep floating on
    // screen — SovosDialog renders through a portal to document.body and isn't
    // affected by the parent's `display: none`. Close transient action dialogs
    // when the tab becomes inactive so they don't linger over the other tab.
    useEffect(() => {
        if (isActive) return;
        setShowConfirmDialog(false);
        setShowSkipProceedDialog(false);
    }, [isActive]);

    useEffect(() => {
        if (!onStepChange) return;
        onStepChange({
            step,
            frameworkName,
            entityLabel,
            imported,
            hasPersistedMapping: Boolean(savedMappingId),
            mappingLoading: loadingExistingMapping,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, frameworkName, entityLabel, imported, savedMappingId, loadingExistingMapping]);

    useEffect(() => {
        if (!savedMappingId || !selectedConfigSet || !selectedEntityName) return;
        if (!onMappingSaved) return;
        onMappingSaved({
            mappingId: savedMappingId,
            configSet: selectedConfigSet,
            entityName: selectedEntityName,
            fileType: selectedFileType,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedMappingId, selectedConfigSet, selectedEntityName, selectedFileType]);

    useEffect(() => {
        let cancelled = false;
        setSavedMappingId(null);
        setImported(false);
        setPersistedPreloadError(false);
        setPersistedPreloadPending(false);
        persistedPreloadRef.current = null;
        persistedPreloadRequestRef.current += 1;
        entityFieldsRequestRef.current += 1;

        const restoreDraft = (draft) => {
            setMappings(draft.mappings);
            originalMappingsRef.current = draft.mappings;
            setSelectedConfigSet(draft.selectedConfigSet);
            setSelectedEntityName(draft.selectedEntityName);
            setSelectedFileType(draft.selectedFileType);
            setEntityLabel(draft.entityLabel);
            setStep(FLOW_STEP_INDEX.DefineMapping);
            setLoadingMschFields(true);
            const requestId = ++entityFieldsRequestRef.current;
            onFetchCdmFields(
                draft.selectedConfigSet,
                draft.selectedEntityName,
                draft.selectedFileType,
            )
                .then((result) => {
                    if (cancelled || requestId !== entityFieldsRequestRef.current) return;
                    setMschFields(toMschFields(result?.items));
                })
                .catch(() => {})
                .finally(() => {
                    if (!cancelled && requestId === entityFieldsRequestRef.current) {
                        setLoadingMschFields(false);
                    }
                });
        };

        if (!profileId || !onFetchExistingMapping) {
            setLoadingExistingMapping(false);
            const draft = profileId ? loadDraft(profileId) : null;
            setIsFirstMapping(!draft);
            if (draft) {
                restoreDraft(draft);
            }
            return () => {
                cancelled = true;
            };
        }
        setLoadingExistingMapping(true);
        onFetchExistingMapping(profileId)
            .then((existing) => {
                if (cancelled) return;
                if (!existing) {
                    setSavedMappingId(null);
                    setImported(false);
                    const draft = loadDraft(profileId);
                    setIsFirstMapping(!draft);
                    if (draft) {
                        restoreDraft(draft);
                    } else {
                        setStep(FLOW_STEP_INDEX.ChooseEntity);
                    }
                    return;
                }
                setIsFirstMapping(false);
                clearDraft(profileId);
                let parsedMappings = [];
                try {
                    parsedMappings = JSON.parse(existing.column_mappings || "[]");
                } catch {
                    parsedMappings = [];
                }
                const restoredMappings = parsedMappings.reduce(
                    (acc, { sourceColumn, targetField }) => ({
                        ...acc,
                        // Backend persists the literal "DO_NOT_IMPORT"; translate it
                        // back to the frontend sentinel so the dropdown recognizes it.
                        [sourceColumn]:
                            targetField === "DO_NOT_IMPORT" ? DO_NOT_IMPORT : targetField,
                    }),
                    {},
                );
                setSelectedConfigSet(existing.config_set);
                setSelectedEntityName(existing.entity_name);
                setSelectedFileType(existing.file_type || "");
                setEntityLabel(existing.entity_name);
                setSavedMappingId(existing.id);
                setMappings(restoredMappings);
                originalMappingsRef.current = restoredMappings;
                persistedPreloadRef.current = { existing, restoredMappings };
                preloadPersistedFields(persistedPreloadRef.current);
                setStep(FLOW_STEP_INDEX.DefineMapping);
            })
            .catch(() => {
                if (!cancelled) setStep(FLOW_STEP_INDEX.ChooseEntity);
            })
            .finally(() => {
                if (!cancelled) setLoadingExistingMapping(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileId]);

    // Columns that have never had a value in any upload for this profile
    // default to "Do not import" instead of forcing the user to skip them
    // one by one. Only fills gaps (columns still unset once any existing
    // mapping/draft has finished loading) — never overrides a real decision.
    useEffect(() => {
        if (isFirstMapping) return;
        if (!columnsWithData) return;
        if (loadingExistingMapping || loadingMschFields) return;
        setMappings((prev) => {
            let changed = false;
            const next = { ...prev };
            csvColumns.forEach((col) => {
                if (next[col] === undefined && !columnsWithData.has(col)) {
                    next[col] = DO_NOT_IMPORT;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [isFirstMapping, columnsWithData, loadingExistingMapping, loadingMschFields, csvColumns]);

    const usedFields = Object.entries(mappings)
        .filter(([, v]) => v && v !== DO_NOT_IMPORT)
        .map(([, v]) => v);

    const totalColumns = csvColumns.length;
    const doNotImportCount = csvColumns.filter((c) => mappings[c] === DO_NOT_IMPORT).length;
    const mappedCount = csvColumns.filter(
        (c) => mappings[c] && mappings[c] !== DO_NOT_IMPORT,
    ).length;
    const needsMappingCount = totalColumns - mappedCount - doNotImportCount;
    const readyCount = mappedCount;
    const skippedCount = doNotImportCount;

    const allMapped = needsMappingCount === 0;

    const handleEntityNextFromHeader = useCallback(() => {
        entitySelectionRef.current?.();
    }, []);

    useEffect(() => {
        if (!onActionStateChange) return;
        onActionStateChange({
            step,
            imported,
            allMapped,
            savingMapping,
            loadingAiMapping,
            selectedEntityName,
            entityCanProceed,
            onChangeEntity: () => setStep(FLOW_STEP_INDEX.ChooseEntity),
            onCancel: () => setStep(FLOW_STEP_INDEX.ChooseEntity),
            onEntityNext: handleEntityNextFromHeader,
            onNext: handleNext,
            onSuggestMapping: onSuggestMapping && selectedEntityName ? handleSuggestMapping : null,
            onStepClick: handleStepClick,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, imported, allMapped, savingMapping, loadingAiMapping, selectedEntityName, entityCanProceed, handleEntityNextFromHeader]);

    const handleMappingChange = useCallback(
        (csvCol, fieldId) => {
            trackUserAction("mapping_column_overridden", {
                csv_column: csvCol,
                new_target_field: fieldId,
                was_ai_suggested: aiSuggestedColumns.has(csvCol),
            });
            setMappings((prev) => {
                const next = { ...prev, [csvCol]: fieldId };
                saveDraft(profileId, {
                    mappings: next,
                    selectedConfigSet,
                    selectedEntityName,
                    selectedFileType,
                    entityLabel,
                });
                return next;
            });
            setAiSuggestedColumns((prev) => {
                if (!prev.has(csvCol)) return prev;
                const next = new Map(prev);
                next.delete(csvCol);
                return next;
            });
            setAiSuggestionMeta((prev) => {
                if (!prev.has(csvCol)) return prev;
                const next = new Map(prev);
                next.delete(csvCol);
                return next;
            });
        },
        [
            aiSuggestedColumns,
            profileId,
            selectedConfigSet,
            selectedEntityName,
            selectedFileType,
            entityLabel,
        ],
    );

    const handleRevert = useCallback((csvCol) => {
        const original = originalMappingsRef.current[csvCol] ?? null;
        setMappings((prev) => {
            const next = { ...prev };
            if (original === null) {
                delete next[csvCol];
            } else {
                next[csvCol] = original;
            }
            return next;
        });
    }, []);

    const handleCancelAiMapping = useCallback(() => {
        aiMappingRequestRef.current += 1;
        setLoadingAiMapping(false);
    }, []);

    const handleSuggestMapping = async () => {
        if (!onSuggestMapping || !profileId || !selectedConfigSet || !selectedEntityName) return;
        const requestId = ++aiMappingRequestRef.current;
        trackUserAction("mapping_suggestion_requested", {
            profile_id: profileId,
            config_set: selectedConfigSet,
            entity_name: selectedEntityName,
            csv_column_count: csvColumns.length,
        });
        setLoadingAiMapping(true);
        setAiSuggestionMeta(new Map());
        setAiSuggestedColumns(new Map());
        setMappingSuggestError(null);
        try {
            const result = await onSuggestMapping(
                profileId,
                selectedConfigSet,
                selectedEntityName,
                selectedFileType,
            );
            if (requestId !== aiMappingRequestRef.current) return;
            if (!result) {
                setMappingSuggestError(
                    i18n`Could not load proposed column links. Try again or map each column in the grid.`,
                );
                return;
            }
            const rows = result.column_mappings || [];
            let skippedNoCsvMatch = 0;
            const suggestionMeta = new Map();
            const suggestedMappings = {};
            rows.forEach(({ source_column, target_field, display_name, score, rationale }) => {
                if (!source_column || !target_field) return;
                const matchingCol = csvColumns.find(
                    (col) => col === source_column || stripCdmPrefix(col) === source_column,
                );
                if (!matchingCol) {
                    skippedNoCsvMatch += 1;
                    return;
                }
                const gridId = resolveAiTargetToGridFieldId(target_field, mschFields);
                suggestionMeta.set(matchingCol, {
                    gridId,
                    displayName: display_name,
                    score: score ?? 0,
                    rationale,
                });
                suggestedMappings[matchingCol] = gridId;
            });
            if (requestId !== aiMappingRequestRef.current) return;
            const nextMappings = { ...mappingsRef.current, ...suggestedMappings };
            mappingsRef.current = nextMappings;
            setMappings(nextMappings);
            saveDraft(profileId, {
                mappings: nextMappings,
                selectedConfigSet,
                selectedEntityName,
                selectedFileType,
                entityLabel,
            });
            setAiSuggestionMeta(suggestionMeta);
            setAiSuggestedColumns(
                new Map([...suggestionMeta.entries()].map(([col, meta]) => [col, meta.score ?? 0])),
            );
            setAiAppliedSkippedCsv(skippedNoCsvMatch);
            setAiAppliedCount(suggestionMeta.size);
            if (suggestionMeta.size > 0) {
                trackUserAction("mapping_suggestion_applied", {
                    profile_id: profileId,
                    applied_count: suggestionMeta.size,
                    skipped_csv_mismatch: skippedNoCsvMatch,
                });
                setShowAiAppliedToast(true);
            }
        } catch {
            if (requestId !== aiMappingRequestRef.current) return;
            setMappingSuggestError(
                i18n`Could not load proposed column links. Try again or map each column in the grid.`,
            );
        } finally {
            if (requestId === aiMappingRequestRef.current) {
                setLoadingAiMapping(false);
            }
        }
    };

    const handleEntityChosen = async ({ configSet, entityName, fileType, entityLabel: label }) => {
        const requestId = ++entityFieldsRequestRef.current;
        trackUserAction("mapping_started", {
            config_set: configSet,
            entity_name: entityName,
            file_type: fileType,
        });
        setSelectedConfigSet(configSet);
        setSelectedEntityName(entityName);
        setSelectedFileType(fileType);
        setEntityLabel(label);
        setStep(FLOW_STEP_INDEX.DefineMapping);
        saveDraft(profileId, {
            mappings,
            selectedConfigSet: configSet,
            selectedEntityName: entityName,
            selectedFileType: fileType,
            entityLabel: label,
        });
        setLoadingMschFields(true);
        try {
            const result = await onFetchCdmFields(configSet, entityName, fileType);
            if (requestId !== entityFieldsRequestRef.current) return;
            if (result?.file_type) {
                setSelectedFileType(result.file_type);
            }
            setMschFields(toMschFields(result?.items));
        } finally {
            if (requestId === entityFieldsRequestRef.current) {
                setLoadingMschFields(false);
            }
        }
    };

    const handleNext = () => {
        if (step === FLOW_STEP_INDEX.DefineMapping) setShowConfirmDialog(true);
    };

    const handleConfirm = async () => {
        setShowConfirmDialog(false);
        let result;
        try {
            result = await saveMapping();
        } catch {
            return;
        }
        if (!result?.success) {
            return;
        }
        setShowSuccessToast(true);
        onMappingConfirmed?.();
    };

    const handleStepClick = (stepNum) => {
        if (stepNum === FLOW_STEP_INDEX.ChooseEntity) return;
        setStep(stepNum);
    };

    const remainingWarningRows = [];

    const skippedColumns = csvColumns.filter((c) => mappings[c] === DO_NOT_IMPORT);

    const validationColumnsForDialog = csvColumns.filter(
        (c) => mappings[c] && mappings[c] !== DO_NOT_IMPORT,
    );

    const dialogTableColumns = validationColumnsForDialog;

    const saveMapping = async (markImported = false) => {
        setSavingMapping(true);
        // Persist "Do not import" too (as the backend's own DO_NOT_IMPORT
        // literal), so reopening this mapping later shows it as explicitly
        // skipped instead of forgetting the decision and reverting to unmapped.
        const columnMappings = csvColumns
            .filter((col) => mappings[col])
            .map((col) => ({
                sourceColumn: col,
                targetField: mappings[col] === DO_NOT_IMPORT ? "DO_NOT_IMPORT" : mappings[col],
            }));

        const serializedMappings = JSON.stringify(columnMappings);
        const isUpdate = !!savedMappingId;

        try {
            const result = savedMappingId
                ? await onUpdateMapping(savedMappingId, projectId, {
                      configSet: selectedConfigSet,
                      entityName: selectedEntityName,
                      columnMappings: serializedMappings,
                      fileType: selectedFileType,
                  })
                : await onCreateMapping({
                      profileId,
                      projectId,
                      mappingName: `${profileId}_${selectedConfigSet}_${selectedEntityName}`,
                      configSet: selectedConfigSet,
                      entityName: selectedEntityName,
                      columnMappings: serializedMappings,
                      fileType: selectedFileType,
                  });

            if (result?.success) {
                clearDraft(profileId);
                setSavedMappingId(result.id || savedMappingId);
                trackUserAction("mapping_saved", {
                    profile_id: profileId,
                    mapping_id: result.id || savedMappingId,
                    is_update: isUpdate,
                    mapped_count: columnMappings.length,
                    skipped_count: skippedCount,
                });
                if (markImported) {
                    setImported(true);
                }
            }

            return result;
        } finally {
            setSavingMapping(false);
        }
    };

    const handleSkipAndProceed = async () => {
        setShowSkipProceedDialog(false);
        const result = await saveMapping();
        if (result?.success) {
            onMappingConfirmed?.();
        }
    };

    const visibleCsvColumns = hideDoNotImport
        ? csvColumns.filter((c) => mappings[c] !== DO_NOT_IMPORT)
        : csvColumns;

    const visibleHeaderItems = userHeaderItems.filter((h) =>
        visibleCsvColumns.includes(h.columnName),
    );
    const columnWidth = 220;
    const dgHeader = visibleHeaderItems.map((h) => ({
        ...h,
        disableColumnMenu: true,
        label: [toSourceColumnLabel(h.headerName || h.columnName)],
        minWidth: columnWidth,
        sortable: false,
        width: columnWidth,
    }));
    const dgItems = buildDgItems(items, visibleCsvColumns);

    const dgAvailableFilters = visibleCsvColumns.map((col) => {
        const fieldId = mappings[col];
        const field =
            fieldId && fieldId !== DO_NOT_IMPORT ? mschFields.find((f) => f.id === fieldId) : null;
        const filterType =
            field?.type === "number" ? "int" : field?.type === "date" ? "date" : "string";
        return { prop: col, type: filterType, name: col };
    });

    return (
        <SovosBox
            sx={{
                boxSizing: "border-box",
                height: "100%",
                minWidth: 0,
                overflowX: "auto",
                p: 3,
                position: "relative",
                width: "100%",
            }}
        >
            {step === FLOW_STEP_INDEX.DefineMapping && persistedPreloadError && (
                <SovosBox sx={{ mb: 2 }}>
                    <InlineLoadError
                        i18n={i18n}
                        onRetry={() => {
                            if (persistedPreloadRef.current) {
                                preloadPersistedFields(persistedPreloadRef.current);
                            }
                        }}
                    />
                </SovosBox>
            )}
            <SovosBox
                sx={{
                    opacity: step === FLOW_STEP_INDEX.DefineMapping && !entityLabel ? 0.4 : 1,
                    pointerEvents:
                        step === FLOW_STEP_INDEX.DefineMapping && !entityLabel ? "none" : "auto",
                    transition: "opacity 0.2s ease",
                    display:
                        imported || step === FLOW_STEP_INDEX.ChooseEntity ? "none" : undefined,
                }}
            >
                {step === FLOW_STEP_INDEX.DefineMapping && (
                    <StepMapColumns
                        needsMappingCount={needsMappingCount}
                        readyCount={readyCount}
                        skippedCount={skippedCount}
                        totalColumns={totalColumns}
                        mappings={mappings}
                        mschFields={mschFields}
                        usedFields={usedFields}
                        aiSuggestedColumns={aiSuggestedColumns}
                        aiSuggestionMeta={aiSuggestionMeta}
                        aiAppliedSkippedCsv={aiAppliedSkippedCsv}
                        loadingAiMapping={loadingAiMapping}
                        onCancelAiMapping={handleCancelAiMapping}
                        originalMappingsRef={originalMappingsRef}
                        hideDoNotImport={hideDoNotImport}
                        showDoNotImportToggle={step === FLOW_STEP_INDEX.DefineMapping}
                        dgItems={dgItems}
                        dgHeader={dgHeader}
                        dgAvailableFilters={dgAvailableFilters}
                        i18n={i18n}
                        entityLabel={entityLabel}
                        onChangeEntity={() => setStep(FLOW_STEP_INDEX.ChooseEntity)}
                        loading={
                            loadingExistingMapping ||
                            loadingMschFields ||
                            (!!entityLabel && mschFields.length === 0)
                        }
                        onMappingChange={handleMappingChange}
                        onRevert={handleRevert}
                        onToggleHideDoNotImport={() => setHideDoNotImport((h) => !h)}
                    />
                )}
            </SovosBox>

            {step === FLOW_STEP_INDEX.ChooseEntity && !imported && (
                <ChooseEntityCard
                    onNext={handleEntityChosen}
                    onCanProceedChange={setEntityCanProceed}
                    onRegisterProceed={(proceed) => {
                        entitySelectionRef.current = proceed;
                    }}
                    disableCancel={persistedPreloadPending}
                    preloadError={persistedPreloadError}
                    onRetryPreload={() => {
                        if (persistedPreloadRef.current) {
                            preloadPersistedFields(persistedPreloadRef.current);
                        }
                    }}
                    i18n={i18n}
                    projectId={projectId}
                    onFetchMappingContexts={onFetchMappingContexts}
                    onFetchEntities={onFetchEntities}
                    initialConfigSet={selectedConfigSet}
                    initialEntityName={selectedEntityName}
                    initialFileType={selectedFileType}
                    profileId={profileId}
                    projectModules={projectModules}
                    onSuggestEntity={onSuggestEntity}
                />
            )}

            <ConfirmDialog
                open={showConfirmDialog}
                onCancel={() => setShowConfirmDialog(false)}
                onConfirm={handleConfirm}
                mappings={mappings}
                mschFields={mschFields}
                i18n={i18n}
            />

            <WarningSkipDialog
                open={showSkipProceedDialog}
                onCancel={() => setShowSkipProceedDialog(false)}
                onConfirm={handleSkipAndProceed}
                skippedColumns={skippedColumns}
                warningRows={remainingWarningRows}
                tableColumns={dialogTableColumns}
                i18n={i18n}
            />

            <SovosSnackbar
                open={showSuccessToast}
                message={i18n`Mapping saved. Validate and fix the data before proceeding.`}
                onClose={() => setShowSuccessToast(false)}
                autoHideDuration={5000}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
            <SovosSnackbar
                open={showAiAppliedToast}
                message={
                    i18n`Copied ${aiAppliedCount} proposed links from the table into Map to.` +
                    (aiAppliedSkippedCsv > 0
                        ? ` ${i18n`${aiAppliedSkippedCsv} rows did not match a column name.`}`
                        : "") +
                    ` ${i18n`Use the summary list and each dropdown to confirm.`}`
                }
                onClose={() => setShowAiAppliedToast(false)}
                autoHideDuration={7000}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
            <SovosSnackbar
                open={!!mappingSuggestError}
                message={mappingSuggestError}
                variant="error"
                timeout="medium"
                onClose={() => setMappingSuggestError(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </SovosBox>
    );
};

export default ColumnMappingCanvas;
